import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  useTheme,
  Chip,
  Rating
} from '@mui/material';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsIcon from '@mui/icons-material/Directions';
import { searchEmergencyServices } from './utils/googleMapsLoader';

/**
 * EmergencyServicesSearch Component - Allows searching for nearby emergency services by location
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Function to close the dialog
 * @param {Function} onSelect - Function to select a service
 * @param {string} initialLocation - Initial location to search for
 * @param {string} serviceType - Type of emergency service to search for (police, fire, ambulance)
 * @returns {JSX.Element} - Rendered component
 */
const EmergencyServicesSearch = ({ open, onClose, onSelect, initialLocation = '', serviceType = 'police' }) => {
  const [location, setLocation] = useState(initialLocation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState(serviceType);
  const theme = useTheme();

  // Map tab names to Google Places service types
  const getServiceType = (tabName) => {
    const serviceMap = {
      police: 'police',
      fire: 'fire_station',
      ambulance: 'ambulance'
    };
    return serviceMap[tabName] || 'police';
  };

  const handleSearch = async () => {
    // Validate location input
    if (!location || !location.trim()) {
      setError('Please enter a location to search for emergency services.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setServices([]);
    
    try {
      // Use Google Maps Places API to search for emergency services
      const serviceType = getServiceType(activeTab);
      console.log('Searching for:', serviceType, 'near:', location.trim());
      
      const results = await searchEmergencyServices(location.trim(), serviceType, 15000); // 15km radius
      
      if (results && results.length > 0) {
        setServices(results);
        setSelectedService(null);
      } else {
        setError(`No ${activeTab} services found near "${location.trim()}". Try a different location or expand your search area.`);
      }
    } catch (err) {
      console.error('Error fetching emergency services:', err);
      const errorMessage = err.message || 'Unknown error occurred';
      setError(`Failed to fetch ${activeTab} services: ${errorMessage}. Please check your location and try again.`);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectService = (service) => {
    setSelectedService(service);
  };

  const handleConfirmSelection = () => {
    if (selectedService) {
      onSelect(selectedService, activeTab);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setServices([]);
    setSelectedService(null);
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'police':
        return <LocalPoliceIcon sx={{ color: theme.palette.info.main }} />;
      case 'fire':
        return <LocalFireDepartmentIcon sx={{ color: theme.palette.error.main }} />;
      case 'ambulance':
        return <LocalHospitalIcon sx={{ color: theme.palette.success.main }} />;
      default:
        return <LocalPoliceIcon />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Search for Local Emergency Services
        </Typography>
      </DialogTitle>
      
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab 
          value="police" 
          label="Police" 
          icon={<LocalPoliceIcon />} 
          iconPosition="start"
          sx={{ 
            fontWeight: activeTab === 'police' ? 'bold' : 'normal',
            color: activeTab === 'police' ? theme.palette.info.main : 'inherit'
          }}
        />
        <Tab 
          value="fire" 
          label="Fire Department" 
          icon={<LocalFireDepartmentIcon />} 
          iconPosition="start"
          sx={{ 
            fontWeight: activeTab === 'fire' ? 'bold' : 'normal',
            color: activeTab === 'fire' ? theme.palette.error.main : 'inherit'
          }}
        />
        <Tab 
          value="ambulance" 
          label="Ambulance" 
          icon={<LocalHospitalIcon />} 
          iconPosition="start"
          sx={{ 
            fontWeight: activeTab === 'ambulance' ? 'bold' : 'normal',
            color: activeTab === 'ambulance' ? theme.palette.success.main : 'inherit'
          }}
        />
      </Tabs>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={9}>
              <TextField
                fullWidth
                label="Enter Location (City, State or ZIP)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                variant="outlined"
                placeholder="e.g., Atlanta, GA or 30303"
                InputProps={{
                  startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading || !location.trim()}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 2, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee',
              borderRadius: '8px'
            }}
          >
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {services.length > 0 ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
              {`${services.length} ${activeTab === 'police' ? 'Police Stations' : activeTab === 'fire' ? 'Fire Departments' : 'Ambulance Services'} found near ${location}`}
            </Typography>
            
            <List sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)', borderRadius: '8px' }}>
              {services.map((service) => (
                <React.Fragment key={service.id}>
                  <ListItem 
                    button 
                    selected={selectedService?.id === service.id}
                    onClick={() => handleSelectService(service)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: selectedService?.id === service.id 
                        ? (theme.palette.mode === 'dark' ? 'rgba(30, 136, 229, 0.2)' : 'rgba(30, 136, 229, 0.1)')
                        : 'transparent',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 136, 229, 0.1)' : 'rgba(30, 136, 229, 0.05)'
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getServiceIcon(activeTab)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {service.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={`${service.distance} km`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            {service.rating && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Rating 
                                  value={service.rating} 
                                  readOnly 
                                  size="small" 
                                  precision={0.1}
                                />
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  ({service.userRatingsTotal || 0})
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}>
                            <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.875rem', mt: 0.2 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                              {service.address}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.875rem' }} />
                            <Typography variant="body2" color="text.secondary">
                              {service.phoneNumber}
                            </Typography>
                          </Box>
                          {service.website && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <DirectionsIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.875rem' }} />
                              <Typography 
                                variant="body2" 
                                component="a" 
                                href={service.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: theme.palette.primary.main,
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                Visit Website
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    {selectedService?.id === service.id && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DirectionsIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Use coordinates if available for more accurate directions
                          const query = service.location 
                            ? `${service.location.lat},${service.location.lng}` 
                            : encodeURIComponent(service.address);
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${query}`, '_blank');
                        }}
                        sx={{ ml: 2 }}
                      >
                        Directions
                      </Button>
                    )}
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </Box>
        ) : !loading && location && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No services found. Try searching for a different location.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          color="primary"
          disabled={!selectedService}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmergencyServicesSearch;
